// SPDX-License-Identifier: MIT
import { supabase } from './supabase';
import type { Locale } from '../i18n/detect';
import type { MagnifyResult } from './magnify';

// One conversational turn's result. `manifest` + `version_number` are present
// ONLY when the model materially evolved the fiche (operator-decided 2026-05-24:
// "le modèle décide" — pure-discussion turns leave the fiche + version history
// untouched). Mirrors the converse edge fn contract (supabase/functions/converse).
export interface ConverseResult {
  conversation_id: string;
  reply: string;
  manifest_changed: boolean;
  version_number?: number;
  manifest?: MagnifyResult;
}

const STUB_LATENCY_MS = 900;

// Calls the Tier 1 Edge Function `converse`. Deployed manually (gated) per the
// sub-project-2 design. Errors propagate with `.status` attached (same shape as
// magnifyIdea) so the chat can branch on 403 ai_not_configured / 429.
//
//   VITE_CONVERSE_STUB=true in .env.local → deterministic stub (no Anthropic,
//   no DB): a message that reads like a feature change ("ajoute"/"add"/…)
//   returns a manifest_changed turn so the "✓ fiche → vN" badge + fiche
//   re-render path can be dogfooded; everything else returns a plain reply.
export async function converse(
  projectId: string,
  message: string,
  locale: Locale,
  conversationId?: string,
  currentManifest?: MagnifyResult,
  currentVersion?: number,
): Promise<ConverseResult> {
  if (import.meta.env.VITE_CONVERSE_STUB === 'true') {
    return stubConverse(projectId, message, locale, conversationId, currentManifest, currentVersion);
  }

  const body: Record<string, unknown> = { project_id: projectId, message, locale };
  if (conversationId) body.conversation_id = conversationId;

  const { data, error } = await supabase.functions.invoke<ConverseResult>('converse', { body });
  if (error) {
    const ctx = (error as { context?: Response }).context;
    const status = ctx?.status;
    const wrapped = new Error(error.message) as Error & { status?: number };
    if (typeof status === 'number') wrapped.status = status;
    throw wrapped;
  }
  if (!data) throw new Error('Empty response from converse function');
  return data;
}

const CHANGE_HINTS = /(ajoute|ajouter|rajoute|enl[èe]ve|supprime|remplace|change|add|remove|replace|page|stat|feature|fonctionnalit)/i;

async function stubConverse(
  projectId: string,
  message: string,
  locale: Locale,
  conversationId: string | undefined,
  currentManifest: MagnifyResult | undefined,
  currentVersion: number | undefined,
): Promise<ConverseResult> {
  await new Promise((r) => setTimeout(r, STUB_LATENCY_MS));
  const convId = conversationId ?? `stub-conv-${projectId}`;
  const changed = CHANGE_HINTS.test(message) && !!currentManifest;
  if (!changed) {
    return {
      conversation_id: convId,
      reply: locale === 'fr'
        ? 'Bonne question — dis-m\'en un peu plus et j\'ajuste la fiche si besoin.'
        : 'Good question — tell me a bit more and I\'ll tweak the sheet if needed.',
      manifest_changed: false,
    };
  }
  const base = currentManifest!;
  const addedBullet = locale === 'fr'
    ? `L'utilisateur peut : ${message.trim()}`
    : `The user can: ${message.trim()}`;
  return {
    conversation_id: convId,
    reply: locale === 'fr'
      ? `C'est ajouté à la fiche — ${message.trim()}.`
      : `Added to the sheet — ${message.trim()}.`,
    manifest_changed: true,
    version_number: (currentVersion ?? 1) + 1,
    manifest: { ...base, outline: [...base.outline, addedBullet] },
  };
}
